import { createFileRoute } from "@tanstack/react-router";
import { PHOTOGRAPHER } from "@/content/foto";
import { FotoFilmShowcase } from "@/components/site/foto/FotoFilmShowcase";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoRail } from "@/components/site/foto/PhotoRail";
import { coverPhotoFor, galleryPhotos } from "@/content/photo-gallery";
import {
  aeoGeo,
  breadcrumbs,
  imageGallery,
  faqPage,
  serviceSchema,
  person,
  speakable,
} from "@/lib/seo/schema";

const COVER = coverPhotoFor("event");

const PATH = "/foto/eventfoto";
const TITLE = "Eventfotograf — bilder samma dag som eventet";
const DESCRIPTION =
  "Eventfotograf för konferens, mässa och kick-off: första urvalet publicerbart redan under dagen, hela leveransen inom 2–3 arbetsdagar. Se exempel och boka.";

const FAQ: LandingFAQ[] = [
  {
    q: "Hur snabbt får vi bilderna från eventet?",
    a: "Vi kan leverera ett första urval löpande under dagen — färgsatta och webbanpassade — så att kommunikationsavdelningen kan publicera medan eventet pågår. Fullständig leverans kommer normalt inom 2–3 arbetsdagar.",
  },
  {
    q: "Hur många bilder levereras från ett event?",
    a: "Från en halvdag levereras normalt 60–120 bilder, från en heldag 150–300. Vi gallrar hårt: dubbletter och missar sorteras bort så att ni får en användbar mapp, inte ett arkiv.",
  },
  {
    q: "Fotograferar ni även talare och scen?",
    a: "Ja. Ett typiskt eventuppdrag täcker scen och talare, publik och stämning, mingel och samtal, samt detaljer som scenografi, skyltning och mat. Vi går igenom bildlistan innan dagen.",
  },
  {
    q: "Behöver ni fotografera i förväg?",
    a: "Vid större event är det värdefullt att vara på plats 30–60 minuter innan öppning för att fotografera lokalen tom och riggad — de bilderna används ofta i efterrapport och nästa års inbjudan.",
  },
  {
    q: "Hur hanterar ni GDPR och deltagare som inte vill synas?",
    a: "Vi rekommenderar tydlig skyltning vid entrén och en markering — exempelvis en annan färg på namnbrickan — för dem som avböjer. Vi respekterar markeringen vid fotografering och i urvalet.",
  },
];

export const Route = createFileRoute("/foto/eventfoto")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Eventfotograf — Fenny Foto" },
      { property: "og:description", content: "Konferens, mässa och företagsevent dokumenterat samma dag." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({
            path: PATH,
            name: TITLE,
            description: DESCRIPTION,
            question: "Vad gör en eventfotograf och hur snabbt kommer bilderna?",
            answer:
              "En eventfotograf dokumenterar scen, publik, mingel, talare och detaljer utifrån en bildlista som sätts före dagen. Fenny levererar ett första urval löpande under eventet så att kommunikation och sociala kanaler kan publicera i realtid, och full leverans inom 2–3 arbetsdagar: 60–120 bilder vid halvdag och 150–300 vid heldag. Offert per uppdrag. Kontakt: Fenny, hey@fenny.se.",
            speakableSelectors: ["[data-quick-answer]"],
          })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Eventfotograf",
            path: PATH,
            serviceType: "Eventfotografering",
            description: "Fotografering av konferens, mässa, kick-off och företagsevent med snabbleverans.",
            areaServed: ["Stockholm", "Göteborg", "Malmö", "Uppsala", "Västerås", "Sverige"],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          imageGallery({
            name: TITLE,
            path: PATH,
            description: "Event- och reportagebilder ur Fennys portfolio.",
            images: galleryPhotos.slice(0, 12),
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          person({
            name: PHOTOGRAPHER.name,
            jobTitle: PHOTOGRAPHER.role,
            email: PHOTOGRAPHER.email,
            path: PATH,
            description: PHOTOGRAPHER.bio,
            knowsAbout: [
              "Eventfoto",
              "Konferensfotograf",
              "Mässfotografering",
              "Företagsevent",
              "Fotograf Stockholm",
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Foto", path: "/foto" },
            { name: "Eventfoto", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      heroImage={{ src: COVER.src, alt: COVER.alt }}
      eyebrow="Eventfoto"
      title={<>Eventbilder som går att publicera medan eventet pågår.</>}
      lede="Vi dokumenterar konferenser, mässor, kick-offer och kundevent — scen, publik, mingel och detaljer. Ett första urval levereras löpande under dagen så att kommunikation och sociala kanaler kan arbeta i realtid."
      tldr={
        <p>
          Eventfoto med <strong>löpande leverans under dagen</strong> och full
          leverans inom <strong>2–3 arbetsdagar</strong>. Halvdag ger normalt{" "}
          <strong>60–120 bilder</strong>, heldag <strong>150–300</strong>.
          Offert per uppdrag.
        </p>
      }
      ctaPrimary={{ label: "Begär offert", to: "#offert" }}
      ctaSecondary={{ label: "Eventfilm", to: "/eventfilm" }}
      trackingId="foto_eventfoto"
      stills={false}
      videos={false}
      afterHero={
        <PhotoRail
          photos={galleryPhotos.slice(0, 10)}
          eyebrow="Reportage"
          heading="Dokumentärt öga, redaktionell disciplin"
        />
      }
      bullets={[
        { title: "Snabburval samma dag", body: "Färgsatta webbfiler medan eventet pågår — inte veckan efter." },
        { title: "Bildlista i förväg", body: "Vi vet vilka personer, moment och sponsorer som måste finnas med." },
        { title: "Diskret närvaro", body: "Vi arbetar med tyst utrustning och stör inte program eller samtal." },
      ]}
      process={{
        heading: "Så arbetar vi under eventet",
        steps: [
          { label: "Avstämning", body: "Körschema, bildlista, viktiga personer och sponsorkrav.", duration: "innan dagen" },
          { label: "Före öppning", body: "Tom lokal, scenografi, skyltning och riggat material.", duration: "30–60 min" },
          { label: "Under eventet", body: "Scen, publik, mingel, detaljer — enligt körschema.", duration: "löpande" },
          { label: "Snabburval", body: "10–20 bilder redigerade och skickade under dagen.", duration: "löpande" },
          { label: "Full leverans", body: "Gallrat, retuscherat och levererat i webb- och tryckformat.", duration: "2–3 dagar" },
        ],
      }}
      sections={[
        {
          id: "foto-och-film-pa-event",
          heading: "Foto och film på samma event",
          body: (
            <p>
              Behöver ni både bilder och en aftermovie? Vi bemannar med fotograf
              och filmteam samtidigt, med gemensam bildlista och ett manér. Se{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/eventfilm">
                eventfilm
              </a>{" "}
              och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/livesandning">
                livesändning
              </a>
              .
            </p>
          ),
        },
        {
          id: "eventtyper",
          heading: "Event vi ofta fotograferar",
          body: (
            <p>
              Kundkonferenser och partnerdagar, mässmontrar, produktlanseringar,
              kick-offer och interna dagar, prisutdelningar och jubileum, samt
              seminarier och paneler. Vid mässa lönar det sig att dela dagen i
              två pass — ett för monter och besökare, ett för scen och möten.
            </p>
          ),
        },
      ]}
      afterSections={<FotoFilmShowcase offset={5} heading="Event i rörligt och stillbild" lede="Eventfilm för efterhandsspridning och eventfoto för snabb publicering — samma team på plats." />}
      faq={FAQ}
      riskReducers
      closing={{
        title: "Har ni ett event inbokat?",
        body: "Skicka datum, plats och körschema så återkommer vi med upplägg och fast pris.",
        primary: { label: "Begär offert", to: "#offert" },
      }}
      contactSlot={<FotoQuoteForm location="foto_eventfoto" defaultAssignment="eventfoto" />}
    />
  );
}
